import React, { useEffect } from 'react';
import { useFormikContext, Field, FieldArray, ErrorMessage } from 'formik';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import * as Tgcc from '../../tgccGlobal';
import {
  Section,
  Title,
  Label,
  Input,
  AutoTextArea,
  Select,
  SecondaryButton,
  PrimaryButton,
  DesignItem,
  DesignCodeInputContainer,
  DesignPreviewContainer,
  DesignPreviewLabel,
  Thumbnail,
  ThumbnailPlaceholder,
  LoadingContainer,
  SuccessBadge,
  SectionHeader,
  NavigationButtons,
  CompactRow,
  ApparelItem,
  RemoveButton,
  ApparelGrid,
} from '../../styles/Catalog.styles';
import {
  FaPlus,
  FaTrash,
  FaSearch,
  FaArrowRight,
  FaTshirt,
  FaCheckCircle
} from 'react-icons/fa';

interface CatalogStep1Props {
  fetchDesign: (code: string) => void;
  loadingDesigns: Record<string, boolean>;
  designThumbnails: Record<string, string>;
  openDesignSelector: (appIdx: number, desIdx: number) => void;
  nextStep: () => void;
  requestType: string;
}

export const CatalogStep1: React.FC<CatalogStep1Props> = ({
  fetchDesign,
  loadingDesigns,
  designThumbnails,
  openDesignSelector,
  nextStep,
  requestType,
}) => {
  const isWebsite = requestType === 'Website';
  const dispatch = useDispatch();
  const activeProof = useSelector((s: Tgcc.GlobalState) => s.ActiveProof, shallowEqual);
  const { values } = useFormikContext<{
    apparel: Array<{ style?: string; color?: string; designs: any[] }>;
    notes: string;
    previousPurchases?: string;
  }>();

  useEffect(() => {
    dispatch({
      type: Tgcc.StateAction.SetActiveProof,
      payload: {
        ...(activeProof ?? new Tgcc.Proof()),
        apparelItems: values.apparel,
        notes: values.notes,
        previousPurchases: values.previousPurchases || '',
      },
    });
  }, [
    dispatch,
    activeProof,
    JSON.stringify(values.apparel),
    values.notes,
    values.previousPurchases,
  ]);

  return (
    <Section
      variants={{
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
      }}
      initial="hidden"
      animate="visible"
    >
      <Title>
        <FaTshirt /> {isWebsite ? 'Designs' : 'Apparel Items'}
      </Title>

      {isWebsite ? (
        <>
          <div style={{ marginTop: '1.5rem' }}>
            <Label>Overall Notes</Label>
            <Field
              name="notes"
              as={AutoTextArea}
              placeholder="Tell us what you need!"
              rows={5}
            />
          </div>

          <NavigationButtons>
            <div />
            <PrimaryButton type="button" onClick={nextStep}>
              Next <FaArrowRight />
            </PrimaryButton>
          </NavigationButtons>
        </>
      ) : (
        <>
          <FieldArray name="apparel">
            {apparelHelpers => (
              <>
                <ApparelGrid>
                  {values.apparel.map((item, i) => {
                    const heading =
                      item.style && item.color
                        ? `${item.style} – ${item.color}`
                        : `Item #${i + 1}`;

                    return (
                      <ApparelItem key={i}>
                        <CompactRow>
                          <h3>{heading}</h3>
                          <RemoveButton
                            type="button"
                            onClick={() => apparelHelpers.remove(i)}
                          >
                            <FaTrash size={12} /> Remove
                          </RemoveButton>
                        </CompactRow>

                        <CompactRow>
                          <div style={{ flex: 1 }}>
                            <Label>Style</Label>
                            <Field
                              name={`apparel.${i}.style`}
                              as={Input}
                              placeholder="e.g., GDH100"
                            />
                            <ErrorMessage name={`apparel.${i}.style`}>
                              {msg => <div>⚠ {msg}</div>}
                            </ErrorMessage>
                          </div>
                          <div style={{ flex: 1 }}>
                            <Label>Color</Label>
                            <Field
                              name={`apparel.${i}.color`}
                              as={Input}
                              placeholder="e.g., Red"
                            />
                            <ErrorMessage name={`apparel.${i}.color`}>
                              {msg => <div>⚠ {msg}</div>}
                            </ErrorMessage>
                          </div>
                        </CompactRow>

                        <FieldArray name={`apparel.${i}.designs`}>
                          {designHelpers => (
                            <>
                              <SectionHeader>
                                <Label>Designs</Label>
                                {item.designs.length > 0 && (
                                  <SuccessBadge>
                                    <FaCheckCircle /> {item.designs.length}
                                  </SuccessBadge>
                                )}
                              </SectionHeader>

                              {item.designs.map((d, j) => (
                                <DesignItem key={j}>
                                  <CompactRow>
                                    <h4>Design #{j + 1}</h4>
                                    <RemoveButton
                                      type="button"
                                      onClick={() => designHelpers.remove(j)}
                                    >
                                      <FaTrash size={12} /> Remove
                                    </RemoveButton>
                                  </CompactRow>

                                  <div style={{ marginBottom: '1rem' }}>
                                    <Label>Position</Label>
                                    <Field
                                      name={`apparel.${i}.designs.${j}.position`}
                                      as={Select}
                                    >
                                      <option value="">— Select —</option>
                                      <option value="LC">Left Chest</option>
                                      <option value="BACK">Back</option>
                                      <option value="FRONT">Front</option>
                                    </Field>
                                    <ErrorMessage name={`apparel.${i}.designs.${j}.position`}>
                                      {msg => <div>⚠ {msg}</div>}
                                    </ErrorMessage>
                                  </div>

                                  <CompactRow>
                                    <div style={{ flex: 1 }}>
                                      <Label>Code</Label>
                                      <DesignCodeInputContainer>
                                        <Field
                                          name={`apparel.${i}.designs.${j}.code`}
                                          as={Input}
                                          placeholder="Design code"
                                          onBlur={e =>
                                            fetchDesign((e.target as HTMLInputElement).value)
                                          }
                                        />
                                        <SecondaryButton
                                          type="button"
                                          onClick={() => openDesignSelector(i, j)}
                                        >
                                          <FaSearch />
                                        </SecondaryButton>
                                      </DesignCodeInputContainer>
                                      <ErrorMessage name={`apparel.${i}.designs.${j}.code`}>
                                        {msg => <div>⚠ {msg}</div>}
                                      </ErrorMessage>
                                    </div>

                                    <DesignPreviewContainer>
                                      {loadingDesigns[d.code] ? (
                                        <LoadingContainer>
                                          <img src="/images/loading.gif" alt="Loading…" />
                                        </LoadingContainer>
                                      ) : designThumbnails[d.code] ? (
                                        <>
                                          <Thumbnail
                                            src={designThumbnails[d.code]}
                                            onClick={() =>
                                              window.open(designThumbnails[d.code], '_blank')
                                            }
                                          />
                                          <DesignPreviewLabel>Enlarge</DesignPreviewLabel>
                                        </>
                                      ) : (
                                        <ThumbnailPlaceholder>No preview</ThumbnailPlaceholder>
                                      )}
                                    </DesignPreviewContainer>
                                  </CompactRow>

                                  <div>
                                    <Label>Notes</Label>
                                    <Field
                                      name={`apparel.${i}.designs.${j}.notes`}
                                      as={AutoTextArea}
                                      placeholder="Special instructions"
                                      rows={3}
                                    />
                                  </div>
                                </DesignItem>
                              ))}

                              <SecondaryButton
                                type="button"
                                onClick={() =>
                                  designHelpers.push({ position: '', code: '', notes: '' })
                                }
                              >
                                <FaPlus /> Add Design
                              </SecondaryButton>
                            </>
                          )}
                        </FieldArray>
                      </ApparelItem>
                    );
                  })}
                </ApparelGrid>

                <PrimaryButton
                  type="button"
                  onClick={() =>
                    apparelHelpers.push({ style: '', color: '', designs: [] })
                  }
                >
                  <FaPlus /> Add Item
                </PrimaryButton>
              </>
            )}
          </FieldArray>

          <div style={{ marginTop: '1.5rem' }}>
            <Label>Previously Purchased</Label>
            <Field
              name="previousPurchases"
              as={AutoTextArea}
              placeholder="Want to include any previously purchased weborders?"
              rows={4}
            />
          </div>

          <div style={{ marginTop: '1.5rem' }}>
            <Label>Overall Notes</Label>
            <Field
              name="notes"
              as={AutoTextArea}
              placeholder="Overall instructions"
              rows={4}
            />
          </div>

          <NavigationButtons>
            <div />
            <PrimaryButton type="button" onClick={nextStep}>
              Next <FaArrowRight />
            </PrimaryButton>
          </NavigationButtons>
        </>
      )}
    </Section>
  );
};
